import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'redux/store';
import { FaArrowLeft } from 'react-icons/fa';
import { getStarsRate, setStarsRate, StarsRate } from '../../../api/AdminAPI';
import { ADMIN_DASHBOARD_PATH } from 'router/adminPath';

/**
 * Set how many Telegram Stars make one US dollar.
 *
 * Spec: specs/active/2026-08-26-stars-rate-and-admin-guard/ (US4).
 *
 * Two deliberate choices here, both because this field is a price:
 *
 * - **Provenance is shown, not hidden.** `source` says whether the number is a
 *   deliberate setting, the environment, or the built-in fallback. Without it
 *   "the rate is 100" cannot be checked — on 2026-09-02 production turned out
 *   to be priced by an exception handler rather than by any configuration, and
 *   it took a live investigation to find that out.
 * - **The delay is stated.** Bots read the rate on their refresh tick, so a
 *   change is not instant. Saying so beats an admin concluding it did not work
 *   and setting it twice.
 *
 * Validation is deliberately NOT duplicated here: the server refuses a bad rate
 * before storing and returns its own wording. A second client-side rule would
 * drift from it and start disagreeing about what is valid.
 */
/**
 * How far the rate may move before the screen says something.
 *
 * 1.2x, not 2x: the real mistake this guards against was 100 -> 75 (1.33x), and
 * a 2x threshold slept through it. A SMALLER number is MORE generous — 50
 * over-credits 2x, 75 over-credits 1.33x — which is the counter-intuition that
 * makes a wide threshold dangerous here.
 */
const BIG_CHANGE_FACTOR = 1.2;

const StarsRatePanel = () => {
  const navigate = useNavigate();
  const apiKey = useSelector((state: RootState) => state.userData.apiKey);

  const [current, setCurrent] = useState<StarsRate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState('');
  // Load and save errors are separate on purpose. save() reloads afterwards so
  // the screen shows what is actually in force, and a shared error slot would
  // let that reload wipe the refusal message the admin still needs to read.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!apiKey) return;
    setLoading(true);
    const res = await getStarsRate(apiKey);
    if (res.success && res.data) {
      setCurrent(res.data);
      setDraft(String(res.data.rate));
      setLoadError(null);
    } else {
      // Never render a plausible-looking default here — an admin would take it
      // for the live value and reason about a price that is not in force.
      setCurrent(null);
      setLoadError(res.error || 'Could not read the current rate');
    }
    setLoading(false);
  }, [apiKey]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!apiKey) return;
    setSaving(true);
    setSaveError(null);
    setSaved(null);

    const res = await setStarsRate(apiKey, Number(draft));
    // Reload either way, so the screen shows what is in force rather than the
    // draft — then report, so the reload cannot swallow the message.
    await load();
    if (res.success && res.data) {
      setSaved(res.data.propagation);
    } else {
      setSaveError(res.error || 'Could not save the rate');
    }
    setSaving(false);
  };

  /**
   * A change this large is more often a typo than an intention.
   *
   * Not a block — the server accepts any positive number by an explicit
   * decision, and second-guessing that here would put two different rules in
   * front of the same field. This only makes the size of the change visible
   * before it is made.
   *
   * Threshold tightened from 2x to 1.2x on 2026-09-03, by measurement rather
   * than taste: the rate was set to 75 on production that day, which credits
   * $0.0133 per Star against a ~$0.009 mobile payout — a loss on every mobile
   * top-up — and 100 -> 75 is only 1.33x, so the 2x warning stayed silent
   * through the exact mistake it was written for.
   */
  const parsedDraft = Number(draft);
  const bigChange =
    current !== null &&
    draft !== '' &&
    Number.isFinite(parsedDraft) &&
    parsedDraft > 0 &&
    (parsedDraft / current.rate >= BIG_CHANGE_FACTOR ||
      current.rate / parsedDraft >= BIG_CHANGE_FACTOR);

  const sourceLabel: Record<StarsRate['source'], string> = {
    stored: 'set here',
    env: 'from the server environment',
    default: 'built-in fallback — nothing is configured',
  };

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Flex justify="space-between" align="center" mb={6} gap={2} wrap="wrap">
        <Heading size="lg">Telegram Stars rate</Heading>
        <Button
          leftIcon={<FaArrowLeft />}
          variant="outline"
          size="sm"
          onClick={() => navigate(ADMIN_DASHBOARD_PATH)}
        >
          Dashboard
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner size="lg" />
        </Flex>
      ) : (
        <Box maxW="lg">
          {current && (
            <Box mb={5}>
              <Text fontSize="sm" mb={1}>
                In force now
              </Text>
              <Flex align="center" gap={3} wrap="wrap">
                <Text fontSize="2xl" fontWeight="bold">
                  {current.rate} ⭐ = $1
                </Text>
                <Badge
                  colorScheme={current.source === 'stored' ? 'green' : 'orange'}
                >
                  {sourceLabel[current.source]}
                </Badge>
              </Flex>
            </Box>
          )}

          <FormControl mb={4}>
            <FormLabel htmlFor="stars-rate">Stars per US dollar</FormLabel>
            <Input
              id="stars-rate"
              type="number"
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              isDisabled={saving}
            />
            <Text fontSize="sm" mt={2}>
              Bots apply a change on their next refresh, within about a minute —
              it is not instant.
            </Text>
          </FormControl>

          {bigChange && (
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              That is a large change from {current?.rate}. Double-check it — a
              wrong rate here changes what every top-up credits, and a SMALLER
              number credits MORE per Star.
            </Alert>
          )}

          <Button
            colorScheme="blue"
            onClick={save}
            isLoading={saving}
            isDisabled={draft === '' || draft === String(current?.rate ?? '')}
          >
            Save rate
          </Button>

          {(saveError || loadError) && (
            <Alert status="error" mt={4} borderRadius="md">
              <AlertIcon />
              {saveError || loadError}
            </Alert>
          )}
          {saved && (
            <Alert status="success" mt={4} borderRadius="md">
              <AlertIcon />
              {saved}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StarsRatePanel;
